import { fromJS, List as ImmutableList, Map as ImmutableMap } from 'immutable'
import moment from 'moment-timezone'
import { faCalendarClock } from '@fortawesome/pro-solid-svg-icons/faCalendarClock'
import { faTelescope } from '@fortawesome/pro-solid-svg-icons/faTelescope'
import { faWave } from '@fortawesome/pro-solid-svg-icons/faWave'

import {
  AlertActionTypes,
  AlertCheckTypes,
  AlertScheduleTypes,
  AlertTriggerTypes,
  EmptyAlert,
  NewsPageModules
} from 'static/constants'
import { listOrEmpty, listUnique } from 'utils/immutable'

const alertTypeIconConfig = {
  [AlertTriggerTypes.NEW_HITS]: faTelescope,
  [AlertTriggerTypes.SCHEDULE]: faCalendarClock,
  [AlertTriggerTypes.THRESHOLD]: faWave,
  [AlertTriggerTypes.WITHOUT_CONDITION]: faCalendarClock
}

export const getAlertTypeIcon = type => alertTypeIconConfig[type] || faTelescope

export const mergeAlert = alert => {
  const incoming = ImmutableMap.isMap(alert) ? alert : fromJS(alert)
  let selected = fromJS(EmptyAlert).mergeDeep(incoming)

  const listKeys = ['scheduledJobs', 'actions']
  listKeys.forEach(key => {
    if (incoming.has(key)) {
      selected = selected.set(key, incoming.get(key))
    }
  })

  if (incoming.has('schedule')) {
    selected = selected.set('schedule', incoming.get('schedule'))
  }

  if (incoming.has('condition')) {
    selected = selected.set('condition', incoming.get('condition'))
  }

  if (!(selected.get('nameEn') || '').trim() && selected.get('name')) {
    selected = selected.set('nameEn', selected.get('name'))
  }

  return selected
}

export const alertFromSavedSearch = (savedSearch, timezone) => {
  let alert = fromJS(EmptyAlert)
  const locale = savedSearch.get('locale') || 'de'
  const nameKeys = ['name', 'nameEn', 'nameDe', 'nameFr', 'nameRu', 'nameZh', 'nameJa', 'nameIt']

  nameKeys.forEach(key => {
    const value = savedSearch.get(key)

    if (value) {
      alert = alert.set(key, value)
    }
  })

  if (!(alert.get('nameEn') || '').trim()) {
    alert = alert.set('nameEn', savedSearch.get('name') || '')
  }

  return alert
    .setIn(['schedule', 'timezone'], timezone || alert.getIn(['schedule', 'timezone']))
    .setIn(['condition', 'savedSearchId'], savedSearch.get('id'))
    .update('actions', actions => actions.map(action => action
      .set('savedSearchId', savedSearch.get('id'))
      .set('locale', locale)))
}

export const alertChartSavedSearchIds = alert => listUnique(
  listOrEmpty(alert.get('actions'))
    .flatMap(action => listOrEmpty(action.get('charts')))
    .map(chart => (
      chart.getIn(['dataSourceOpts', 'savedSearchId']) || chart.get('savedSearchId') || null
    ))
    .filter(Boolean)
    .map(String)
)

export const alertExpiresAtFromPicker = (value, timezone) => {
  if (!value) {
    return null
  }

  const zone = timezone || 'Europe/Berlin'
  const clock = moment(value).format('YYYY-MM-DD HH:mm:ss')
  const expiresAt = moment.tz(clock, zone)

  if (!expiresAt.isValid()) {
    return null
  }

  return expiresAt.format()
}

export const alertExpiresAtPickerValue = (expiresAt, timezone) => {
  if (!expiresAt) {
    return null
  }

  const date = moment.tz(expiresAt, timezone || 'Europe/Berlin')

  if (!date.isValid()) {
    return null
  }

  return date.format('YYYY-MM-DD HH:mm:ss')
}

export const formatAlertExpiresAt = (expiresAt, timezone) => {
  if (!expiresAt) {
    return null
  }

  const date = moment.tz(expiresAt, timezone || 'Europe/Berlin')

  if (!date.isValid()) {
    return null
  }

  return date.format('LLL')
}

export const isAlertExpired = (expiresAt, now = moment()) => {
  if (!expiresAt) {
    return false
  }

  const date = moment(expiresAt)

  return date.isValid() && !date.isAfter(now)
}

export const getAlertSavedSearchLabel = (savedSearch, i18n) => {
  if (!savedSearch) {
    return '—'
  }

  const moduleName = savedSearch.get('moduleName')
  let moduleLabel = i18n.get(moduleName)

  if (moduleName === NewsPageModules.NEWS) {
    moduleLabel = i18n.get('news_board')
  }

  if (moduleName === NewsPageModules.GAIO) {
    moduleLabel = i18n.get('geo_analysis')
  }

  return `${moduleLabel} | ${savedSearch.get('name')}`
}

export const triggerSummary = (alert, i18n) => {
  const scheduleType = alert.getIn(['schedule', 'type'])
  const check = alert.getIn(['condition', 'checks', 0])
  const checkType = check ? check.get('type') : AlertTriggerTypes.NEW_HITS

  if (scheduleType === AlertScheduleTypes.CALENDAR) {
    const times = (alert.getIn(['schedule', 'times']) || ImmutableList()).toJS().join(', ')

    return times
      ? i18n.get('alert_when_schedule_at', { times })
      : i18n.get('alert_when_schedule')
  }

  if (checkType === AlertTriggerTypes.THRESHOLD) {
    return i18n.get('alert_when_threshold_value', { value: (check && check.get('value')) || 0 })
  }

  if (!check) {
    return i18n.get('alert_without_condition')
  }

  return alert.getIn(['schedule', 'interval']) === 'PT1H'
    ? i18n.get('alert_when_new_results_hourly')
    : i18n.get('alert_when_new_results_fast')
}

export const alertConditionSummary = (condition, i18n) => {
  const check = condition.getIn(['checks', 0])
  const checkType = check && check.get('type')

  if (checkType === AlertCheckTypes.THRESHOLD) {
    const thresholdSummary = i18n.get('alert_when_threshold_value', { value: check.get('value') || 0 })

    return `${i18n.get('alert_when_threshold')}: ${thresholdSummary}`
  }

  if (checkType === AlertCheckTypes.NEW_HITS) {
    return i18n.get('alert_when_new_results')
  }

  return `${i18n.get('alert_without_condition')}. ${i18n.get('alert_without_condition_hint')}`
}

const scheduleDaysSummary = (days, i18n) => {
  const selectedDays = [...new Set(days.filter(day => Number.isInteger(day) && day >= 1 && day <= 7))]
    .sort((left, right) => left - right)

  if (selectedDays.length === 0 || selectedDays.length === 7) {
    return i18n.get('alert_schedule_every_day')
  }

  const labels = selectedDays.map(day => moment().isoWeekday(day).format('dddd'))

  if (selectedDays.length === 1) {
    return i18n.get('alert_schedule_on_day', { day: labels[0] })
  }

  const consecutive = selectedDays.every((day, index) => index === 0 || day === selectedDays[index - 1] + 1)

  if (consecutive) {
    return i18n.get('alert_schedule_from_days', { from: labels[0], to: labels[labels.length - 1] })
  }

  return i18n.get('alert_schedule_on_days', { days: labels.join(', ') })
}

export const scheduleSummary = (alert, i18n) => {
  const schedule = alert.get('schedule')
  const scheduleType = schedule.get('type') || AlertScheduleTypes.INTERVAL
  const days = listOrEmpty(schedule.get('days')).toJS()
  const daySummary = scheduleDaysSummary(days, i18n)

  if (scheduleType === AlertScheduleTypes.CALENDAR) {
    return i18n.get('alert_schedule_summary_calendar', {
      days: daySummary,
      times: listOrEmpty(schedule.get('times')).toJS().join(', ')
    })
  }

  const activeWindow = listOrEmpty(schedule.get('activeWindows')).first()
  const activeWindowSummary = activeWindow
    ? i18n.get('alert_schedule_active_window_summary', {
      from: activeWindow.get('from'),
      to: activeWindow.get('to')
    })
    : ''

  return i18n.get('alert_schedule_summary_interval', {
    frequency: schedule.get('interval') === 'PT1H'
      ? i18n.get('alert_schedule_every_hour')
      : i18n.get('alert_schedule_near_real_time'),
    days: daySummary,
    activeWindow: activeWindowSummary
  })
}

const rowAlertType = alert => {
  if (alert.getIn(['schedule', 'type']) === AlertScheduleTypes.CALENDAR) {
    return AlertTriggerTypes.SCHEDULE
  }

  return alert.getIn(['condition', 'checks', 0, 'type']) || AlertTriggerTypes.WITHOUT_CONDITION
}

const listSize = value => (ImmutableList.isList(value) ? value.size : 0)

export const activeAiSummaryCount = action => {
  const newsSummaryCount = action.getIn(['content', 'ai', 'summary', 'enabled']) === true ? 1 : 0
  const chartSummaryCount = listOrEmpty(action.get('charts'))
    .filter(chart => chart.getIn(['chartOpts', 'useAiAnalysis']) === true)
    .size

  return newsSummaryCount + chartSummaryCount
}

const truncate = (value, max = 48) => {
  if (value.length <= max) {
    return value
  }

  return `${value.slice(0, max - 1)}…`
}

export const alertActionSummary = (action, i18n) => {
  const parts = []
  const locale = action.get('locale')

  if (locale) {
    parts.push(i18n.get(`language_${locale}`))
  }

  if (action.get('type') === AlertActionTypes.WEBHOOK) {
    const url = (action.get('url') || '').trim()

    if (url) {
      parts.push(truncate(url))
    }
  } else {
    const recipients = listSize(action.get('recipients'))
    const mailingLists = listSize(action.get('mailingListIds'))

    if (recipients) {
      parts.push(`${recipients} ${i18n.get('recipients')}`)
    }

    if (mailingLists) {
      parts.push(`${mailingLists} ${i18n.get('mailing_list')}`)
    }
  }

  const charts = listSize(action.get('charts'))
  parts.push(i18n.get(
    charts === 1 ? 'alert_action_chart_count_one' : 'alert_action_chart_count',
    { count: charts }
  ))

  const attachments = listSize(action.getIn(['content', 'attachments']))
  parts.push(i18n.get(
    attachments === 1 ? 'alert_action_attachment_count_one' : 'alert_action_attachment_count',
    { count: attachments }
  ))

  const ai = action.getIn(['content', 'ai'])

  if (ai && (ai.getIn(['summary', 'enabled']) || ai.getIn(['overall', 'enabled']))) {
    parts.push(i18n.get('ai'))
  }

  return parts.join(' · ')
}

export const selectableAlertSavedSearches = (
  savedSearches,
  selectedSavedSearchId,
  hasNewsPoolSavedSearches,
  hasNewsSavedSearches,
  hasProfileMonitoringSavedSearches,
  hasAnalysisSavedSearches,
  hasGaioModule,
  retainedSavedSearchId = null
) => savedSearches.filter(savedSearch => (
  (
    (hasNewsPoolSavedSearches || savedSearch.get('moduleName') !== NewsPageModules.NEWS_POOL)
    && (hasNewsSavedSearches || savedSearch.get('moduleName') !== NewsPageModules.NEWS)
    && (hasProfileMonitoringSavedSearches || savedSearch.get('moduleName') !== NewsPageModules.PROFILE_MONITORING)
    && (hasAnalysisSavedSearches || savedSearch.get('moduleName') !== NewsPageModules.ANALYSIS)
    && (hasGaioModule || savedSearch.get('moduleName') !== NewsPageModules.GAIO)
  )
  || [selectedSavedSearchId, retainedSavedSearchId].includes(savedSearch.get('id'))
))

export const canExecuteAlertSavedSearch = (
  savedSearch,
  hasNewsPoolSavedSearches,
  hasNewsSavedSearches,
  hasProfileMonitoringSavedSearches,
  hasAnalysisSavedSearches,
  hasGaioModule
) => (
  Boolean(savedSearch)
  && (
    (hasNewsPoolSavedSearches || savedSearch.get('moduleName') !== NewsPageModules.NEWS_POOL)
    && (hasNewsSavedSearches || savedSearch.get('moduleName') !== NewsPageModules.NEWS)
    && (hasProfileMonitoringSavedSearches || savedSearch.get('moduleName') !== NewsPageModules.PROFILE_MONITORING)
    && (hasAnalysisSavedSearches || savedSearch.get('moduleName') !== NewsPageModules.ANALYSIS)
    && (hasGaioModule || savedSearch.get('moduleName') !== NewsPageModules.GAIO)
  )
)

export const mapAlertToRow = (
  alert,
  i18n,
  savedSearchNames = {},
  mailingListNames = {},
  mailingListRecipients = {}
) => {
  const actions = listOrEmpty(alert.get('actions'))
  const aiFeatures = new Set()
  const deliveryFilterValues = new Set()
  const actionNames = actions
    .map(action => (action.get('name') || '').trim())
    .filter(Boolean)
    .toArray()
  let recipients = 0
  let mailingLists = 0
  let webhooks = 0
  let charts = 0
  let attachments = 0

  if (alert.getIn(['condition', 'ai', 'filter', 'enabled'])) {
    aiFeatures.add(i18n.get('use_ai_filter'))
  }

  actions.forEach(action => {
    const ai = action.getIn(['content', 'ai'])

    if (ai && ai.getIn(['summary', 'enabled'])) {
      aiFeatures.add(i18n.get('ai_summary_enabled'))
    }

    if (ai && ai.getIn(['overall', 'enabled'])) {
      aiFeatures.add(i18n.get('ai_summary_overall_enabled'))
    }

    if (listOrEmpty(action.get('charts')).some(chart => chart.getIn(['chartOpts', 'useAiAnalysis']) === true)) {
      aiFeatures.add(i18n.get('alert_chart_ai_analysis_enabled'))
    }

    if (action.get('type') === AlertActionTypes.WEBHOOK) {
      if ((action.get('url') || '').trim()) {
        webhooks += 1
      }

      return
    }

    recipients += listSize(action.get('recipients'))
    mailingLists += listSize(action.get('mailingListIds'))
    charts += listSize(action.get('charts'))
    attachments += listSize(action.getIn(['content', 'attachments']))

    listOrEmpty(action.get('recipients')).forEach(recipient => deliveryFilterValues.add(recipient))
    listOrEmpty(action.get('mailingListIds')).forEach(mailingListId => {
      const mailingListName = mailingListNames[String(mailingListId)]
      const recipientsForMailingList = mailingListRecipients[String(mailingListId)] || []

      if (mailingListName) {
        deliveryFilterValues.add(mailingListName)
      }

      recipientsForMailingList.forEach(recipient => {
        const email = typeof recipient === 'string' ? recipient : recipient.mail

        if (email) {
          deliveryFilterValues.add(email)
        }
      })
    })
  })

  const deliveryParts = []
  const savedSearchId = alert.getIn(['condition', 'savedSearchId'])

  if (recipients) {
    deliveryParts.push(`${recipients} ${i18n.get('recipients')}`)
  }

  if (mailingLists) {
    deliveryParts.push(`${mailingLists} ${i18n.get('mailing_list')}`)
  }

  if (webhooks) {
    deliveryParts.push(`${webhooks} ${i18n.get('webhooks')}`)
  }

  const delivery = deliveryParts.join(' · ') || '—'

  return {
    id: alert.get('id'),
    alertType: rowAlertType(alert),
    name: alert.get('name') || '—',
    staging: Boolean(alert.get('staging')),
    enabled: Boolean(alert.get('enabled')),
    savedSearchId,
    searchName: savedSearchNames[savedSearchId] || '—',
    condition: triggerSummary(alert, i18n),
    delivery,
    deliveryFilterValue: [delivery, ...deliveryFilterValues].join(' '),
    actionCount: actions.size,
    actionNames,
    aiFeatures: [...aiFeatures],
    hasAiFeatures: aiFeatures.size > 0,
    charts,
    attachments
  }
}

export const savedSearchExecutionRoute = savedSearch => {
  const moduleName = savedSearch.get('moduleName')

  if (moduleName === NewsPageModules.NEWS_POOL) {
    return '/app/search_pool'
  }

  if (moduleName === NewsPageModules.ANALYSIS) {
    return '/app/analysis'
  }

  if (moduleName === NewsPageModules.PROFILE_MONITORING) {
    return '/app/profile_monitoring'
  }

  if (moduleName === NewsPageModules.GAIO) {
    return '/app/geo_analysis'
  }

  return '/app/news'
}
